import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 27, 2026';

  return (
    <Box>
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <PrivacyTipIcon color="primary" sx={{ mr: 1, fontSize: '2rem' }} />
          Privacy Policy
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last updated: {lastUpdated}
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        {/* Introduction */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          QuailGame ("we," "our," or "us") operates the FootyManager application. This Privacy Policy 
          explains how we collect, use, and protect your personal information when you use our service.
        </Typography>
        <Typography variant="body1" paragraph>
          By using FootyManager, you agree to the collection and use of information in accordance with this policy.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Information We Collect */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Information We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          We collect the following types of information:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li><strong>Account Information:</strong> When you create an account through Auth0, we receive your name, email address, and profile picture (if provided).</li>
            <li><strong>Team & League Data:</strong> Information you provide about teams, leagues, players, games, and attendance/RSVP responses.</li>
            <li><strong>Usage Data:</strong> Basic information about how you interact with our service to improve functionality.</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* How We Use Your Information */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use your information to:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li>Provide and maintain the FootyManager service</li>
            <li>Send RSVP notifications and team communications</li>
            <li>Track attendance and manage team rosters</li>
            <li>Generate statistics and reports for your teams and leagues</li>
            <li>Improve our service and user experience</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Data Sharing */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Data Sharing
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
          We do not sell, trade, or share your personal information with third parties for marketing purposes.
        </Typography>
        <Typography variant="body1" paragraph>
          Your data may be shared only in the following circumstances:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li><strong>Within Your Organization:</strong> Team managers and league administrators within your organization can view relevant team and player information.</li>
            <li><strong>Public League Information:</strong> If your league is marked as public, standings, schedules, and team rosters may be visible to other users.</li>
            <li><strong>Service Providers:</strong> We use trusted third-party services (Auth0 for authentication, Azure cloud hosting) that may process your data on our behalf.</li>
            <li><strong>Legal Requirements:</strong> If required by law or to protect our rights and safety.</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Data Retention */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your data for as long as your account is active or as needed to provide services. 
          Historical game and attendance data is retained to provide statistics and reports.
        </Typography>
        <Typography variant="body1" paragraph>
          You may request deletion of your account and associated data by contacting us at info@quailgame.com.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Cookies and Local Storage */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Cookies and Local Storage
        </Typography>
        <Typography variant="body1" paragraph>
          We use cookies and browser local storage to:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li>Maintain your login session</li>
            <li>Remember your preferences</li>
            <li>Improve site performance</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Security */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement industry-standard security measures to protect your data. Authentication is handled 
          through Auth0, a trusted identity provider. All data transmission is encrypted using HTTPS.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Children's Privacy */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          FootyManager is not intended for children under 13. We do not knowingly collect personal 
          information from children under 13. If you believe we have collected such information, 
          please contact us immediately.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Your Rights */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Your Rights
        </Typography>
        <Typography variant="body1" paragraph>
          You have the right to:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to data processing</li>
            <li>Export your data</li>
          </ul>
        </Typography>
        <Typography variant="body1" paragraph>
          To exercise these rights, contact us at info@quailgame.com.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Changes to This Policy */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any changes by 
          posting the new policy on this page and updating the "Last updated" date.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Contact Us */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about this Privacy Policy, please contact us at:
        </Typography>
        <Typography variant="body1">
          Email: <a href="mailto:info@quailgame.com">info@quailgame.com</a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicy;
